import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import {
  Pane,
  Paneset,
  Button,
  Loading,
  Icon,
  Layout,
  Callout,
  MultiColumnList,
  Badge,
} from '@folio/stripes/components';

import { AppIcon } from '@folio/stripes/core';
import useBulkSafetyCheck from '../../../hooks/useBulkSafetyCheck';

const BulkSafeDeletePage = ({ basePath }) => {
  const location = useLocation();
  const history = useHistory();
  const [instanceIds, setInstanceIds] = useState([]);
  const [showMessage, setShowMessage] = useState('');

  // Get instance IDs from URL or session storage
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlIds = searchParams.get('ids');

    if (urlIds) {
      const ids = urlIds.split(',').filter(id => id.trim());
      setInstanceIds(ids);
    } else {
      const storedIds = sessionStorage.getItem('bulkInstanceIds');
      if (storedIds) {
        try {
          const ids = JSON.parse(storedIds);
          setInstanceIds(Array.isArray(ids) ? ids : []);
          sessionStorage.removeItem('bulkInstanceIds');
        } catch (error) {
          console.error('Error parsing stored instance IDs:', error);
          setInstanceIds([]);
        }
      }
    }
  }, [location]);

  const { loading, error, results, stats, canDeleteAll, refresh } = useBulkSafetyCheck(instanceIds);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleDelete = async () => {
    if (!canDeleteAll) return;

    setShowMessage('جاري تنفيذ الحذف...');
    // TODO: Implement actual deletion
    setTimeout(() => {
      setShowMessage('تم الحذف بنجاح!');
      setTimeout(() => {
        history.push('/inventory');
      }, 2000);
    }, 3000);
  };

  // Column mapping for the results table
  const columnMapping = {
    status: 'الحالة',
    title: 'العنوان',
    holdings: 'المقتنيات',
    items: 'المواد',
    action: 'الإجراء'
  };

  const renderStatus = (result) => {
    if (result.status === 'error') {
      return <Badge color="red">خطأ</Badge>;
    }
    return result.canDelete ?
      <Badge color="green">آمن للحذف</Badge> :
      <Badge color="red">محظور</Badge>;
  };

  const renderTitle = (result) => {
    return result.instance?.title || 'غير موجود';
  };

  const formatter = {
    status: renderStatus,
    title: renderTitle,
    holdings: (result) => result.holdingsCount || 0,
    items: (result) => result.itemsCount || 0,
    action: (result) => (result.canDelete ? 'سيتم الحذف' :
      result.unavailableItemsCount > 0 ? `${result.unavailableItemsCount} مواد غير متاحة` : 'خطأ')
  };

  // No instances selected
  if (instanceIds.length === 0) {
    return (
      <Paneset>
        <Pane defaultWidth="100%" paneTitle="الحذف المتعدد للتسجيلات">
          <Layout className="textCentered">
            <Icon icon="exclamation-circle" size="large" />
            <p>لم يتم اختيار أي تسجيلات للحذف</p>
            <Button buttonStyle="primary" onClick={handleGoBack}>
              العودة للمخزون
            </Button>
          </Layout>
        </Pane>
      </Paneset>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Paneset>
        <Pane defaultWidth="100%" paneTitle="الحذف المتعدد للتسجيلات">
          <Layout className="textCentered">
            <Loading />
            <p>جاري تحليل {instanceIds.length} تسجيلة للحذف الآمن...</p>
          </Layout>
        </Pane>
      </Paneset>
    );
  }

  // Error state
  if (error) {
    return (
      <Paneset>
        <Pane defaultWidth="100%" paneTitle="الحذف المتعدد للتسجيلات">
          <Layout className="textCentered">
            <Icon icon="exclamation-circle" size="large" status="error" />
            <p>خطأ في التحليل: {error}</p>
            <Button buttonStyle="primary" onClick={refresh}>
              إعادة المحاولة
            </Button>
            <Button onClick={handleGoBack}>
              العودة
            </Button>
          </Layout>
        </Pane>
      </Paneset>
    );
  }

  // Main view
  return (
    <Paneset>
      <Pane
        defaultWidth="100%"
        paneTitle={
          <span>
            <AppIcon app="inventory" iconKey="instance" size="small" />
            الحذف المتعدد للتسجيلات ({stats.total})
          </span>
        }
        firstMenu={
          <Button onClick={handleGoBack}>
            <Icon icon="left-arrow" />
            العودة
          </Button>
        }
        lastMenu={
          <>
            <Button onClick={refresh}>
              <Icon icon="refresh" />
              تحديث
            </Button>
            {canDeleteAll && stats.safe > 0 && (
              <Button buttonStyle="danger" onClick={handleDelete}>
                <Icon icon="trash" />
                حذف {stats.safe} تسجيلة
              </Button>
            )}
          </>
        }
      >
        {/* Summary */}
        <div style={{ padding: '16px', backgroundColor: '#f8f9fa', marginBottom: '16px' }}>
          <h3>ملخص التحليل</h3>
          <div style={{ display: 'flex', gap: '20px' }}>
            <div>
              <strong>المجموع:</strong> {stats.total}
            </div>
            <div style={{ color: 'green' }}>
              <strong>آمن للحذف:</strong> {stats.safe}
            </div>
            <div style={{ color: 'red' }}>
              <strong>محظور:</strong> {stats.blocked}
            </div>
            <div style={{ color: 'orange' }}>
              <strong>أخطاء:</strong> {stats.errors}
            </div>
          </div>
        </div>

        {/* Warning if can't delete all */}
        {!canDeleteAll && (
          <div style={{ padding: '16px', backgroundColor: '#fff3cd', marginBottom: '16px', border: '1px solid #ffeaa7' }}>
            <Icon icon="exclamation-triangle" status="warn" />
            لا يمكن حذف جميع التسجيلات. يجب أن تكون جميع المواد في حالة "متاح" للسماح بالحذف الآمن.
          </div>
        )}

        {/* Results table */}
        <MultiColumnList
          id="bulk-delete-results"
          contentData={results}
          visibleColumns={['status', 'title', 'holdings', 'items', 'action']}
          columnMapping={columnMapping}
          formatter={formatter}
          autosize
          virtualize
        />

        {/* Message callout */}
        {showMessage && (
          <Callout
            type={showMessage.includes('خطأ') ? 'error' : 'success'}
            onClose={() => setShowMessage('')}
          >
            {showMessage}
          </Callout>
        )}
      </Pane>
    </Paneset>
  );
};

BulkSafeDeletePage.propTypes = {
  basePath: PropTypes.string.isRequired,
};

export default BulkSafeDeletePage;
